import {
  Button,
  CloneButton,
  EditButton,
  Icons,
  Input,
  List,
  Modal,
  Space,
  Table,
  TableColumnProps,
  Tooltip,
} from "@pankod/refine-antd";
import { useCustom, useTranslate } from "@pankod/refine-core";
import api from "api/api";
import { defaultButtonFilled18, defaultHeader } from "components";
import { IMetaResponse } from "interfaces";
import { useContext, useEffect, useRef, useState } from "react";
import Highlighter from "react-highlight-words";
import { IAuthContext, AuthContext } from "../../AuthContext";
const { DeleteFilled } = Icons;

const MetaList = () => {
  const t = useTranslate();
  const { languageId } = useContext(AuthContext) as IAuthContext;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const idToDelete = useRef<string | null>(null);
  const [manualLoading, setManualLoading] = useState(false);
  const [tableData, setTableData] = useState<IMetaResponse[]>();
  const columnTitle = [
    {
      label: t("table.name"),
      index: "name",
    },
    {
      label: t("table.id"),
      index: "id",
    },
    {
      label: t("table.pageName"),
      index: "pageName",
    },
  ];
  // search
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<any>(null);
  interface DataType {
    key: string;
    name: string;
    age: number;
    address: string;
  }
  type DataIndex = keyof DataType;

  const { data, isFetching, refetch } = useCustom<IMetaResponse[]>({
    url: `/metatags`,
    method: "get",
    config: {
      filters: [
        {
          field: "languageId",
          value: languageId,
          operator: "eq",
        },
      ],
    },
  });

  useEffect(() => {
    if (data) setTableData(data.data);
  }, [data]);

  async function deleteRecord() {
    setManualLoading((old) => !old);
    await api.metaTags.deleteMetaTag(idToDelete.current as string);
    refetch();
    setManualLoading((old) => !old);
  }

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: any) => void,
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex: any) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div
        style={{
          padding: 8,
        }}
      >
        <Input
          ref={searchInput}
          placeholder={`${t("search")} ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<Icons.SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            {t("buttons.search")}
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            {t("buttons.clear")}
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: any) => (
      <Icons.SearchOutlined
        style={{
          color: filtered ? "#1890ff" : undefined,
        }}
      />
    ),
    onFilter: (value: any, record: any) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownVisibleChange: (visible: any) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text: any) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const sortingData = (item: {
    index: string;
    label: string;
  }): TableColumnProps<IMetaResponse> => {
    let object: TableColumnProps<IMetaResponse> = {};
    object = getColumnSearchProps(item.index);
    return object;
  };

  const columnTitleComponent = columnTitle.map((title) => (
    <Table.Column
      title={title.label}
      key={title.label}
      dataIndex={title.index}
      {...sortingData(title)}
      render={(render) => {
        if (!render && title.label === t("table.pageName"))
          return t("label.allPages");
        if (!render) return "null";
        return String(render);
      }}
    />
  ));
  return (
    <List
      pageHeaderProps={{
        title: defaultHeader(t("meta:list.title")),
      }}
      createButtonProps={{
        style: defaultButtonFilled18,
        children: t("buttons.create"),
      }}
    >
      <Table
        rowKey="id"
        loading={isFetching || manualLoading}
        dataSource={tableData}
        showSorterTooltip={false}
        className="ant-table-custom"
      >
        {columnTitleComponent}
        <Table.Column<IMetaResponse>
          title={t("table.actions")}
          dataIndex="actions"
          render={(_text, record): React.ReactNode => {
            return (
              <Space>
                <Tooltip placement="top" title={t("buttons.edit")}>
                  <EditButton
                    size="large"
                    recordItemId={record.id!}
                    hideText
                    title={t("buttons.edit")}
                    about={t("buttons.edit")}
                    style={{
                      color: "#0e6d33",
                    }}
                  />
                </Tooltip>
                <Tooltip placement="top" title={t("buttons.delete")}>
                  <Button
                    icon={
                      <DeleteFilled
                        twoToneColor="#eb2f96"
                        color="red"
                        style={{
                          color: "#ff4343",
                        }}
                      />
                    }
                    size="large"
                    onClick={() => {
                      setShowDeleteModal(true);
                      idToDelete.current = record.id;
                    }}
                  />
                </Tooltip>
                <Tooltip placement="left" title={t("buttons.clone")}>
                  <CloneButton
                    size="large"
                    recordItemId={record.id!}
                    hideText
                    style={{
                      color: "blue",
                    }}
                  />
                </Tooltip>
              </Space>
            );
          }}
        />
      </Table>
      <Modal
        title={t("meta:list.deleteModal.title")}
        visible={showDeleteModal}
        onOk={async () => {
          if (manualLoading) return;
          setShowDeleteModal(false);
          await deleteRecord();
        }}
        onCancel={() => setShowDeleteModal(false)}
        closable
      >
        {t("meta:list.deleteModal.message")}
      </Modal>
    </List>
  );
};

export { MetaList };
